import { useCallback, useMemo } from "react";
import { get, isArray } from "lodash";

import { CarPark, Dispatcher } from "../../services";
import { useTypedSelector } from "../../redux/store";
import { FleetsToDispatcher } from "../../redux/reducers/account";
import { selectOptionsWrap } from "../../utils";

const useCarParkFilterAccessControl = <
	T,
>(): useCarParkFilterAccessControl.Props<T> => {
	const user = useTypedSelector((state) => state.account.user);

	const {
		isUserFleetOwner,
		idUser,
		accessFleetsIdsOnlyActive,
		accessSetFleetsIdsOnlyActive,
	} = useMemo(() => {
		const isUserFleetOwner =
			Array.isArray(user?.dispatcherToFleets) &&
			(user?.dispatcherToFleets?.length ?? 0) > 0;
		const idUser = user?.id ?? -1;

		const accessFleets: FleetsToDispatcher[] =
			user?.dispatcherToFleets ?? [];

		const accessFleetsOnlyActive: FleetsToDispatcher[] =
			accessFleets.filter((item) => item.fleet?.active);

		const accessFleetsIds: number[] = accessFleets.flatMap((item) =>
			item.fleet?.id ? [item.fleet.id] : [],
		);

		const accessFleetsIdsOnlyActive: number[] =
			accessFleetsOnlyActive.flatMap((item) =>
				item.fleet?.id ? [item.fleet.id] : [],
			);

		const accessSetFleetsIdsOnlyActive: Set<number> = new Set(
			accessFleetsIdsOnlyActive,
		);

		return {
			accessFleets,
			accessFleetsOnlyActive,
			accessFleetsIds,
			accessFleetsIdsOnlyActive,
			isUserFleetOwner,
			idUser,
			accessSetFleetsIdsOnlyActive,
		};
	}, [user]);

	const subCarParkFilterAccess = useCallback(
		({
			models,
			branchPath,
			branchPath2,
			isUserCreated,
			branchPath3,
			isOrderLogic,
			queryId,
			queryIds,
			needFilterWithQerry,
		}: useCarParkFilterAccessControl.Params<T>): T[] => {
			if (!Array.isArray(models) || typeof branchPath !== "string") {
				return [];
			}

			// or filtering by the entered ID of the park, or by the one available through the user parameters (us)
			let arrTypeAccessIds: Set<number>;

			if (typeof queryId === "number") {
				arrTypeAccessIds = new Set([queryId]);
			} else if (Array.isArray(queryIds)) {
				arrTypeAccessIds = new Set(queryIds);
			} else {
				arrTypeAccessIds = accessSetFleetsIdsOnlyActive;
			}

			let accessModels = models;

			// Enable filtering if the user has fleets with him. Or turn on filtering if this is an element that has its own ID on the fleet, on which viewing is allowed
			if (isUserFleetOwner || needFilterWithQerry) {
				accessModels = models.filter((model) => {
					const branchId = get(model, branchPath, -2);
					const idDispatcher = branchPath3
						? get(model, branchPath3, -2)
						: -2;

					// It also allows to see the elements of the array that the current user is responsible for (Only for the account, performer and transport tabs)
					if (isUserCreated && idDispatcher === idUser) return true;

					// Inclusion of logic, if the address path of the object is an array
					if (isArray(branchId)) {
						const arrBranchId = branchId as unknown as any[];
						// Allows you to see the array without machines (required only for 4 tabs on the Order page)
						if (isOrderLogic && arrBranchId.length === 0) {
							return true;
						}
						if (branchPath2) {
							return arrBranchId.some((item) => {
								const nestedId = get(item, branchPath2, -2);

								return (
									typeof nestedId === "number" &&
									arrTypeAccessIds.has(nestedId)
								);
							});
						}

						return (
							arrBranchId.every((id) => typeof id === "number") &&
							arrBranchId.some((id) => arrTypeAccessIds.has(id))
						);
					}

					return arrTypeAccessIds.has(branchId as number);
				});
			}

			return accessModels;
		},
		[accessSetFleetsIdsOnlyActive, idUser, isUserFleetOwner],
	);

	/**
	 * Generates a list of select options from carParks and access carParks.
	 *
	 * @param {useCarParkFilterAccessControl.PropsSubCarParkOptions} params - The parameters for filtering.
	 * @returns {selectOptionsWrap.SelectOption[]} - The formatted list of select options.
	 */
	const subCarParkOptions = useCallback(
		({
			initialCarParkIds,
			models,
		}: useCarParkFilterAccessControl.PropsSubCarParkOptions): selectOptionsWrap.SelectOption[] => {
			const hasOptions = !!initialCarParkIds;
			const carParkList = hasOptions
				? models.filter((item) =>
						initialCarParkIds.includes(item.id),
				  ) || []
				: [];
			const accessCarParkList =
				models.filter((item) =>
					isUserFleetOwner
						? accessFleetsIdsOnlyActive.includes(item.id)
						: true,
				) || [];

			const carParkOptions =
				carParkList?.map((carPark) => {
					const retval = selectOptionsWrap({
						id: carPark.id,
						label: carPark?.name,
					});

					return retval;
				}) || [];

			const accessCarParkOptions =
				accessCarParkList?.map((carPark) => {
					const retval = selectOptionsWrap({
						id: carPark.id,
						label: carPark?.name,
					});

					return retval;
				}) || [];

			return Array.from(
				new Map(
					[...accessCarParkOptions, ...carParkOptions].map((item) => [
						item.key,
						item,
					]),
				).values(),
			);
		},
		[accessFleetsIdsOnlyActive, isUserFleetOwner],
	);

	/**
	 * Generates a list of select options from Dispatcher and access carParks.
	 *
	 * @param {useCarParkFilterAccessControl.PropsSubDispatcherfromCarParkOptions} params - The parameters for filtering.
	 * @returns {selectOptionsWrap.SelectOption[]} - The formatted list of select options.
	 */
	const subDispatcherFromCarParkOptions = useCallback(
		({
			initialCarParkIds,
			models,
			selectIdCarPark,
			isOnFilterOnlyOwnCarParkUser,
		}: useCarParkFilterAccessControl.PropsSubDispatcherfromCarParkOptions): selectOptionsWrap.SelectOption[] => {
			const hasOptions = !!initialCarParkIds;

			// * The rule is only responsible for the fleet or the user created it

			// Shows in the list the one already selected by the selector of the responsible employee
			const currentSelectDispatcherList = selectIdCarPark
				? models.filter((item) => item.id === selectIdCarPark)
				: [];

			// Shows in the list the fleet with users that has already been selected by the fleet selector
			const dispatcherList = hasOptions
				? models.filter((item) =>
						Array.isArray(item.carParks)
							? item.carParks.some((carPark) =>
									initialCarParkIds.includes(carPark.id),
							  )
							: false,
				  ) || []
				: [];

			// Shows in the list of all users if the current one does not have a fleet. Activation only by additional parameter
			const allDispatcherList =
				isOnFilterOnlyOwnCarParkUser && !isUserFleetOwner ? models : [];

			const mapToSelectOption = (item: (typeof models)[number]) =>
				selectOptionsWrap({
					id: item.id,
					label: [
						item.person?.lastName ?? "",
						item.person?.firstName ?? "",
						item.person?.fatherName ?? "",
					]
						.join(" ")
						.trim(),
				});

			const currentSelectDispatcherOptions =
				currentSelectDispatcherList.map(mapToSelectOption);
			const dispatcherOptions = dispatcherList.map(mapToSelectOption);
			const allDispatcherOptions =
				allDispatcherList.map(mapToSelectOption);

			return Array.from(
				new Map(
					[
						...currentSelectDispatcherOptions,
						...dispatcherOptions,
						...allDispatcherOptions,
					].map((item) => [item.key, item]),
				).values(),
			);
		},
		[isUserFleetOwner],
	);

	return useMemo(
		() => ({
			subCarParkFilterAccess,
			isUserFleetOwner,
			accessFleetsIdsOnlyActive,
			subCarParkOptions,
			subDispatcherFromCarParkOptions,
		}),
		[
			subCarParkFilterAccess,
			isUserFleetOwner,
			accessFleetsIdsOnlyActive,
			subCarParkOptions,
			subDispatcherFromCarParkOptions,
		],
	);
};

declare namespace useCarParkFilterAccessControl {
	interface Props<T> {
		subCarParkFilterAccess: (params: Params<T>) => T[];
		isUserFleetOwner: boolean;
		accessFleetsIdsOnlyActive: number[];
		subCarParkOptions: (
			params: PropsSubCarParkOptions,
		) => selectOptionsWrap.SelectOption[];
		subDispatcherFromCarParkOptions: (
			params: PropsSubDispatcherfromCarParkOptions,
		) => selectOptionsWrap.SelectOption[];
	}

	export interface PropsSubCarParkOptions {
		initialCarParkIds?: number[];
		models: CarPark.Model[];
	}

	export interface PropsSubDispatcherfromCarParkOptions {
		initialCarParkIds?: number[];
		models: Dispatcher.Model[];
		selectIdCarPark?: number;
		isOnFilterOnlyOwnCarParkUser?: boolean;
	}

	export interface Params<T> {
		models: T[];
		branchPath: string;
		branchPath2?: string;
		isUserCreated?: boolean;
		branchPath3?: string;
		isOrderLogic?: boolean;
		queryId?: number;
		queryIds?: number[];
		needFilterWithQerry?: boolean;
	}

	interface SmallSubscribeOptions {
		// order?: Role.Order;
		// query?: string;
		// limit?: number;
		// assignableTo?: Role.AssignableTo;
		// taxiServiceIds?: number[];
	}
}

export default useCarParkFilterAccessControl;
